import { create } from 'zustand';
import type { LoginInput, RegisterInput, UserPublic } from '@en3/shared';
import { authApi } from '@/api/auth.api';
import { setAuthReadyWaiter, setRefreshHandler, tryRefreshOnce } from '@/api/client';
import { setAccessToken } from '@/api/token';
import { queryClient } from '@/lib/queryClient';

/**
 * 登录态。**用户信息与访问令牌都在内存里**，刷新页面后通过一次
 * `POST /api/auth/refresh`（HttpOnly Cookie）恢复 —— 这就是"刷新页面登录态保持"的实现方式。
 *
 * 状态机：`idle`（未开始）→ `loading`（正在尝试恢复）→ `authenticated` | `anonymous`。
 * 只有 `authenticated` 才渲染需要登录的内容，避免"闪一下未登录"。
 */
export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  user: UserPublic | null;
  status: AuthStatus;
  /** 应用启动时调用一次：用刷新 Cookie 恢复登录态 */
  bootstrap: () => Promise<void>;
  login: (input: LoginInput) => Promise<UserPublic>;
  register: (input: RegisterInput) => Promise<UserPublic>;
  logout: (allDevices?: boolean) => Promise<void>;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  status: 'idle',

  bootstrap: async () => {
    if (get().status !== 'idle') return;
    set({ status: 'loading' });
    const ok = await tryRefreshOnce();
    if (!ok) set({ user: null, status: 'anonymous' });
  },

  login: async (input) => {
    const session = await authApi.login(input);
    setAccessToken(session.accessToken);
    set({ user: session.user, status: 'authenticated' });
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    return session.user;
  },

  register: async (input) => {
    const session = await authApi.register(input);
    setAccessToken(session.accessToken);
    set({ user: session.user, status: 'authenticated' });
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    return session.user;
  },

  logout: async (allDevices = false) => {
    try {
      await authApi.logout(allDevices);
    } finally {
      // 即便请求失败也要清本地状态，避免"点了退出还显示已登录"
      setAccessToken(null);
      set({ user: null, status: 'anonymous' });
      queryClient.clear();
    }
  },
}));

/**
 * 把「如何刷新」注入给 API 客户端。
 *
 * 客户端在收到 401 时会调用它，成功后重放原请求；这里负责真正调刷新接口、
 * 更新令牌与用户、并在失败时把状态降级为未登录。
 * 放在文件末尾注册，确保 store 已创建（避免 TDZ）。
 */
setRefreshHandler(async () => {
  try {
    const session = await authApi.refresh();
    setAccessToken(session.accessToken);
    useAuthStore.setState({ user: session.user, status: 'authenticated' });
    return true;
  } catch {
    setAccessToken(null);
    useAuthStore.setState({ user: null, status: 'anonymous' });
    return false;
  }
});

/**
 * 把「登录态何时确定」注入给 API 客户端（见 `api/client.ts` 的 `setAuthReadyWaiter`）。
 *
 * 实现方式是**订阅 store 的状态迁移**而不是在各个 action 里手动 resolve：
 * 状态可能由 bootstrap / refresh 处理器 / 登录 / 注册 / 登出等多处改动，
 * 逐个调用迟早会漏；订阅一次就能保证"只要离开 idle/loading 就放行"。
 */
let authSettled = false;
let resolveAuthSettled: (() => void) | null = null;
const authSettledPromise = new Promise<void>((resolve) => {
  resolveAuthSettled = resolve;
});

useAuthStore.subscribe((state) => {
  if (authSettled) return;
  if (state.status === 'idle' || state.status === 'loading') return;
  authSettled = true;
  resolveAuthSettled?.();
});

setAuthReadyWaiter(async () => {
  // 还没开始恢复就先启动一次，避免"没人调用 bootstrap"时永远等下去
  if (useAuthStore.getState().status === 'idle') void useAuthStore.getState().bootstrap();
  await authSettledPromise;
});
